import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  Alert,
  Spinner,
  Table,
  FormGroup,
  Label
} from "reactstrap";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useDispatch } from "react-redux";
import { Fn_FillListData, Fn_DeleteData } from "../../store/Functions";
import { toast } from "react-toastify";
import { Download, FileText, Search, Trash2 } from "react-feather";
import ExcelJS from "exceljs";
import useColumnResize from '../../helpers/useColumnResize'
import '../../helpers/columnResize.css'

const LinkRegisterShow = () => {
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Column definitions & default widths
  const LRS_ALL_COLUMNS = [
    { key: 'Checkbox', label: '', sortKey: null },
    { key: 'CreatedOn', label: 'Created On', sortKey: 'createdOn' },
    { key: 'Period', label: 'Period', sortKey: 'period' },
    { key: 'LinkQty', label: 'Link Qty', sortKey: 'linkQty' },
    { key: 'ContractChain', label: 'Contract Chain', sortKey: null },
    { key: 'Actions', label: 'Actions', sortKey: null },
  ];

  const LRS_DEFAULT_WIDTHS = {
    Checkbox: 30,
    CreatedOn: 160,
    Period: 80,
    LinkQty: 100,
    ContractChain: 400,
    Actions: 90,
  };

  // Column resize feature
  const { columnWidths, handleResizeMouseDown, isResizing } = useColumnResize('linkRegisterShow_columnWidths', LRS_DEFAULT_WIDTHS);

  // ─── Column Drag-to-Reorder Feature ──────────────────────────────
  const LRS_DEFAULT_ORDER = LRS_ALL_COLUMNS.map(c => c.key);
  const LRS_ORDER_KEY = 'linkRegisterShow_columnOrder';

  const lrsGetInitialOrder = () => {
    try {
      const saved = localStorage.getItem(LRS_ORDER_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return [
          ...parsed.filter(k => LRS_DEFAULT_ORDER.includes(k)),
          ...LRS_DEFAULT_ORDER.filter(k => !parsed.includes(k)),
        ];
      }
    } catch (e) { }
    return [...LRS_DEFAULT_ORDER];
  };

  const [lrsColumnOrder, setLrsColumnOrder] = useState(lrsGetInitialOrder);
  const [lrsDragOverKey, setLrsDragOverKey] = useState(null);
  const lrsDragSrcRef = useRef(null);
  const lrsJustDraggedRef = useRef(false);

  const lrsSaveOrder = (order) => {
    try { localStorage.setItem(LRS_ORDER_KEY, JSON.stringify(order)); } catch (e) { }
  };

  const lrsDragStart = (e, key) => {
    lrsDragSrcRef.current = key;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
  };
  const lrsDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (key !== lrsDragSrcRef.current) setLrsDragOverKey(key);
  };
  const lrsDrop = (e, targetKey) => {
    e.preventDefault();
    const srcKey = lrsDragSrcRef.current;
    if (!srcKey || srcKey === targetKey) { setLrsDragOverKey(null); return; }
    setLrsColumnOrder(prev => {
      const next = [...prev];
      const from = next.indexOf(srcKey);
      const to = next.indexOf(targetKey);
      if (from === -1 || to === -1) return prev;
      next.splice(from, 1);
      next.splice(to, 0, srcKey);
      lrsSaveOrder(next);
      return next;
    });
    setLrsDragOverKey(null);
    lrsDragSrcRef.current = null;
    lrsJustDraggedRef.current = true;
    setTimeout(() => { lrsJustDraggedRef.current = false; }, 250);
  };
  const lrsDragEnd = () => {
    if (lrsDragSrcRef.current) {
      lrsJustDraggedRef.current = true;
      setTimeout(() => { lrsJustDraggedRef.current = false; }, 250);
    }
    setLrsDragOverKey(null);
    lrsDragSrcRef.current = null;
  };
  const lrsVisibleColumns = () => lrsColumnOrder
    .map(k => LRS_ALL_COLUMNS.find(c => c.key === k))
    .filter(Boolean);
  // ─── End Column Drag-to-Reorder Feature ──────────────────────────
  const dispatch = useDispatch();

  const API_URL = API_WEB_URLS.MASTER + "/0/token/LinkRegister";
  const API_URL_Delete = API_WEB_URLS.MASTER + "/0/token/DeleteLinkRegister";

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Fn_FillListData(dispatch, setGridData, "gridData", API_URL + "/Id/0");
      setLoading(false);
    };

    fetchData();
  }, [dispatch, API_URL]);

  // Handle mobile-responsive viewport scroll locking to prevent drag-to-refresh
  useEffect(() => {
    const isMobile = () => window.innerWidth <= 768;
    if (isMobile()) {
      document.body.classList.add('no-overscroll');
      document.documentElement.classList.add('no-overscroll');
    }

    const handleResize = () => {
      if (isMobile()) {
        document.body.classList.add('no-overscroll');
        document.documentElement.classList.add('no-overscroll');
      } else {
        document.body.classList.remove('no-overscroll');
        document.documentElement.classList.remove('no-overscroll');
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.classList.remove('no-overscroll');
      document.documentElement.classList.remove('no-overscroll');
    };
  }, [])

  // Handle global navbar refresh event
  useEffect(() => {
    const handleGlobalRefresh = async (e) => {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('app-refresh-start'));
      try {
        await Fn_FillListData(dispatch, setGridData, "gridData", API_URL + "/Id/0");
      } finally {
        window.dispatchEvent(new CustomEvent('app-refresh-end'));
      }
    };
    window.addEventListener('app-refresh-data', handleGlobalRefresh);
    return () => {
      window.removeEventListener('app-refresh-data', handleGlobalRefresh);
    };
  }, [dispatch, API_URL]);

  const btnDeleteOnClick = async (Id) => {
    if (window.confirm("Are you sure you want to delete this link register?")) {
      try {
        setLoading(true);
      
        await Fn_FillListData(dispatch, setGridData, "gridData", API_URL_Delete + "/Id/"+Id);
        
        // Refresh data after delete
          await Fn_FillListData(dispatch, setGridData, "gridData", API_URL + "/Id/0");
        toast.success("Link register deleted successfully");
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("Error deleting link register");
      } finally {
          setLoading(false);
      }
    }
  };

  // Function to parse ContractDetails string
  const parseContractDetails = (contractDetails) => {
    if (!contractDetails) return [];
    
    // Split by " -> " to get individual contracts
    const contracts = contractDetails.split(" -> ");
    
    return contracts.map((contract, index) => {
      const parts = contract.split(" || ");
      const contractObj = {};
      
      parts.forEach(part => {
        const [key, value] = part.split(" : ").map(s => s.trim());
        if (key && value) {
          contractObj[key] = value;
        }
      });
      
      return {
        ...contractObj,
        contractIndex: index + 1
      };
    });
  };

  // Group data by LinkRegisterId - one row per chain
  const chainData = useMemo(() => {
    if (!gridData || gridData.length === 0) return [];
    
    return gridData.map((item) => {
      const contracts = parseContractDetails(item.ContractDetails);
      const chainString = contracts.map((contract, index) => {
        const contractNo = contract.ContractNo || "-";
        const contractDate = contract.ContractDate || "-";
        const rate = contract.Rate || "-";
        const sellerName = contract.SellerName || "-";
        const buyerName = contract.BuyerName || "-";
        return `${contractNo} (Date: ${contractDate}, Rate: ${rate}, ${sellerName} → ${buyerName})`;
      }).join(" → ");
      
      return {
        linkRegisterId: item.LinkRegisterId,
        createdOn: item.CreatedOn || item.createdOn || null,
        period: item.Period || item.period || '',
        linkQty: item.LinkQty || 0,
        chainString: chainString,
        contracts: contracts,
        totalContracts: contracts.length
      };
    });
  }, [gridData]);

  // Helper to check if a name is in full capital letters
  const isFullCapital = (name) => {
    if (!name || typeof name !== 'string') return false;
    const trimmed = name.trim();
    return /[A-Z]/.test(trimmed) && !/[a-z]/.test(trimmed);
  };

  // Helper to render party name with highlight if full capital
  const renderPartyName = (name, type) => {
    if (!name || name === '-') return '-';
    const isCapital = isFullCapital(name);
    const defaultColor = type === 'seller' ? '#dc3545' : '#0066cc';
    const highlightColor = type === 'seller' ? '#c2185b' : '#0d47a1';

    if (isCapital) {
      return (
        <span
          style={{
            backgroundColor: '#fff59d',
            color: highlightColor,
            padding: '1px 5px',
            borderRadius: '3px',
            border: '1px solid #fbc02d',
            fontWeight: '800',
            display: 'inline-block',
            boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title="Full Capital Name"
        >
          {name}
        </span>
      );
    }
    return <span style={{ color: defaultColor, fontWeight: '700' }}>{name}</span>;
  };

  // Helper to render party name HTML for print window
  const renderPartyNameHtml = (name, type) => {
    if (!name || name === '-') return '-';
    const isCapital = isFullCapital(name);
    const defaultColor = type === 'seller' ? '#dc3545' : '#0066cc';
    const highlightColor = type === 'seller' ? '#c2185b' : '#0d47a1';

    if (isCapital) {
      return `<span style="background-color:#fff59d;color:${highlightColor};padding:1px 5px;border-radius:3px;border:1px solid #fbc02d;font-weight:800;display:inline-block;">${name}</span>`;
    }
    return `<span style="color:${defaultColor};font-weight:700;">${name}</span>`;
  };

  // Function to render chain with colored text
  const renderColoredChain = (contracts) => {
    return contracts.map((contract, index) => {
      const contractNo = contract.ContractNo || "-";
      const contractDate = contract.ContractDate || "-";
      const rate = contract.Rate || "-";
      const qty = contract.Qty || contract.LinkQty || "-";
      const sellerName = contract.SellerName || "-";
      const buyerName = contract.BuyerName || "-";

      return (
        <div key={index} style={{ marginBottom: index < contracts.length - 1 ? '5px' : '0', paddingBottom: index < contracts.length - 1 ? '5px' : '0', borderBottom: index < contracts.length - 1 ? '1px dashed #ddd' : 'none' }}>
          {/* Line 1: Contract details */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '2px', fontSize: '11px' }}>
            <span><span style={{ color: '#888', fontWeight: '600' }}>No: </span><span style={{ color: '#333', fontWeight: '700' }}>{contractNo}</span></span>
            <span><span style={{ color: '#888', fontWeight: '600' }}>Dt: </span><span style={{ color: '#856404', fontWeight: '700' }}>{contractDate}</span></span>
            <span><span style={{ color: '#888', fontWeight: '600' }}>Rate: </span><span style={{ color: '#0c5460', fontWeight: '700' }}>₹{rate}</span></span>
            {qty !== "-" && <span><span style={{ color: '#888', fontWeight: '600' }}>Qty: </span><span style={{ color: '#155724', fontWeight: '700' }}>{qty} MT</span></span>}
          </div>
          {/* Line 2: Seller → Buyer */}
          <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
            <span style={{ color: '#888', fontWeight: '600', fontSize: '11px' }}>S: </span>
            {renderPartyName(sellerName, 'seller')}
            <span style={{ color: '#888', fontWeight: '800', margin: '0 3px' }}>→</span>
            <span style={{ color: '#888', fontWeight: '600', fontSize: '11px' }}>B: </span>
            {renderPartyName(buyerName, 'buyer')}
          </div>
        </div>
      );
    });
  };

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return chainData;
    
    const term = searchTerm.toLowerCase();
    return chainData.filter(row => {
      const createdOnStr = row.createdOn 
        ? new Date(row.createdOn).toLocaleDateString('en-GB', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : '';
      return (
        row.chainString.toLowerCase().includes(term) ||
        String(row.linkRegisterId).includes(term) ||
        String(row.linkQty).includes(term) ||
        createdOnStr.toLowerCase().includes(term)
      );
    });
  }, [chainData, searchTerm]);

  // Auto-select all rows when filtered data changes
  useEffect(() => {
    if (filteredData && filteredData.length > 0) {
      const allIds = filteredData.map(row => row.linkRegisterId);
      setSelectedRows(allIds);
      setSelectAll(true);
    } else {
      setSelectedRows([]);
      setSelectAll(false);
    }
  }, [filteredData.length]);

  // Handle select all checkbox
  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      setSelectedRows(filteredData.map(row => row.linkRegisterId));
    } else {
      setSelectedRows([]);
    }
  };

  // Handle individual row selection
  const handleRowSelect = (linkRegisterId) => {
    setSelectedRows(prev => {
      if (prev.includes(linkRegisterId)) {
        const newSelection = prev.filter(id => id !== linkRegisterId);
        setSelectAll(false);
        return newSelection;
      } else {
        const newSelection = [...prev, linkRegisterId];
        if (newSelection.length === filteredData.length) {
          setSelectAll(true);
        }
        return newSelection;
      }
    });
  };

  // Export to Excel
  const handleExcelExport = async () => {
    const selectedData = filteredData.filter(row => selectedRows.includes(row.linkRegisterId));
    
    if (selectedData.length === 0) {
      toast.warning("Please select at least one row to export");
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Link Register');

      // Define columns
      worksheet.columns = [
        { header: 'Created On', key: 'createdOn', width: 18 },
        { header: 'Period', key: 'period', width: 12 },
        { header: 'Link Qty', key: 'linkQty', width: 12 },
        { header: 'Contract Chain', key: 'chainString', width: 80 }
      ];

      // Style header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFF' } };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '28a745' }
      };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow.height = 25;

      // Add data rows (only selected)
      selectedData.forEach((row) => {
        const createdOnFormatted = row.createdOn 
          ? new Date(row.createdOn).toLocaleDateString('en-GB', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })
          : '-';
        
        const excelRow = worksheet.addRow({
          createdOn: createdOnFormatted,
          period: row.period || '-',
          linkQty: parseFloat(row.linkQty) || 0,
          chainString: row.chainString
        });

        excelRow.eachCell({ includeEmpty: true }, (cell) => {
          cell.border = {
            top: { style: 'thin', color: { argb: '000000' } },
            left: { style: 'thin', color: { argb: '000000' } },
            bottom: { style: 'thin', color: { argb: '000000' } },
            right: { style: 'thin', color: { argb: '000000' } }
          };
        });
        
        // Right align numeric columns
        excelRow.getCell('createdOn').alignment = { horizontal: 'center', vertical: 'middle' };
        excelRow.getCell('period').alignment = { horizontal: 'center', vertical: 'middle' };
        excelRow.getCell('linkQty').alignment = { horizontal: 'right', vertical: 'middle' };
        
        // Wrap text for chain column
        excelRow.getCell('chainString').alignment = { wrapText: true, vertical: 'top' };
      });

      // Generate filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const fileName = `Link_Register_${timestamp}.xlsx`;

      // Save the workbook
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Excel file downloaded: ${fileName}`);
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Error exporting to Excel. Please try again.');
    }
  };

  // Export to PDF
  const handlePDFExport = () => {
    const selectedData = filteredData.filter(row => selectedRows.includes(row.linkRegisterId));
    
    if (selectedData.length === 0) {
      toast.warning("Please select at least one row to export");
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    
    if (printWindow) {
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Link Register Report</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              font-size: 12px;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .header h1 {
              margin: 0;
              color: #333;
              font-size: 24px;
            }
            .header p {
              margin: 5px 0;
              color: #666;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
              font-size: 11px;
            }
            th {
              background-color: #28a745;
              color: white;
              font-weight: bold;
              text-align: center;
            }
            .text-center { text-align: center; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Link Register Report</h1>
            <p>Generated on: ${new Date().toLocaleDateString('en-GB')} at ${new Date().toLocaleTimeString('en-GB')}</p>
          </div>
          
          <table>
            <thead>
              <tr>
                <th class="text-center">Created On</th>
                <th class="text-center">Period</th>
                <th class="text-center">Link Qty</th>
                <th>Contract Chain</th>
              </tr>
            </thead>
            <tbody>
              ${selectedData.map(row => {
                const createdOnFormatted = row.createdOn 
                  ? new Date(row.createdOn).toLocaleDateString('en-GB', { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : '-';
                const chainHtml = row.contracts.map((contract, index) => {
                  const contractNo = contract.ContractNo || '-';
                  const contractDate = contract.ContractDate || '-';
                  const rate = contract.Rate || '-';
                  const qty = contract.Qty || contract.LinkQty || null;
                  const sellerName = contract.SellerName || '-';
                  const buyerName = contract.BuyerName || '-';
                  const border = index < row.contracts.length - 1 ? 'border-bottom:1px dashed #ddd;padding-bottom:4px;margin-bottom:4px;' : '';
                  return `<div style="${border}">
                    <div style="font-size:10px;margin-bottom:2px;">
                      <span style="color:#888;font-weight:600;">No: </span><span style="color:#333;font-weight:700;">${contractNo}</span>
                      &nbsp;&nbsp;<span style="color:#888;font-weight:600;">Dt: </span><span style="color:#856404;font-weight:700;">${contractDate}</span>
                      &nbsp;&nbsp;<span style="color:#888;font-weight:600;">Rate: </span><span style="color:#0c5460;font-weight:700;">&#8377;${rate}</span>
                      ${qty ? `&nbsp;&nbsp;<span style="color:#888;font-weight:600;">Qty: </span><span style="color:#155724;font-weight:700;">${qty} MT</span>` : ''}
                    </div>
                    <div style="font-size:11px;">
                      <span style="color:#888;font-weight:600;font-size:10px;">S: </span>${renderPartyNameHtml(sellerName, 'seller')}
                      <span style="color:#888;font-weight:800;margin:0 5px;">&#8594;</span>
                      <span style="color:#888;font-weight:600;font-size:10px;">B: </span>${renderPartyNameHtml(buyerName, 'buyer')}
                    </div>
                  </div>`;
                }).join('');
                return `
                <tr>
                  <td class="text-center">${createdOnFormatted}</td>
                  <td class="text-center">${row.period || '-'}</td>
                  <td class="text-end">${row.linkQty ? parseFloat(row.linkQty).toLocaleString() : '0'}</td>
                  <td>${chainHtml}</td>
                </tr>
              `;
              }).join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;
      
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
            <div className="text-center">
              <Spinner color="primary" size="lg" className="mb-3">
                Loading...
              </Spinner>
              <p className="text-muted">Loading link register data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <style>
        {`
          .col-drag-over {
            outline: 2px solid #FFD700 !important;
            outline-offset: -2px !important;
            background-color: rgba(255, 215, 0, 0.35) !important;
          }
        `}
      </style>
      <div className="container-fluid">
        {/* Page Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="page-title-box d-flex align-items-center justify-content-between">
              <h4 className="mb-0 text-primary fw-semibold">
                <i className="fas fa-link me-2"></i>
                Link Register Show
              </h4>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <Row>
          <Col lg="12">
            <Card className="shadow-sm border-0">
              <CardHeader className="bg-primary text-white py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-semibold">
                    <i className="fas fa-list me-2"></i>
                    Link Register Report
                  </h5>
                  <div className="d-flex gap-2">
                    <Button
                      color="success"
                      size="sm"
                      onClick={handleExcelExport}
                      className="fw-semibold"
                      style={{
                        borderRadius: '6px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Download className="me-2" size={14} />
                      Export Excel
                    </Button>
                  <Button
                      color="danger"
                    size="sm"
                      onClick={handlePDFExport}
                    className="fw-semibold"
                    style={{
                      borderRadius: '6px',
                      transition: 'all 0.3s ease'
                    }}
                  >
                      <FileText className="me-2" size={14} />
                      Export PDF
                  </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody className="p-4">
                {/* Search Section */}
                <Row className="mb-4">
                  <Col lg="6" md="8" sm="12" className="mb-3">
                    <div className="search-box position-relative">
                      <Input
                        type="text"
                        className="form-control form-control-lg border-2"
                        placeholder="Search by Contract No, Seller, Buyer, Created On, or Link Qty..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                          borderRadius: '8px',
                          paddingLeft: '45px',
                          borderColor: '#e9ecef',
                          transition: 'all 0.3s ease'
                        }}
                      />
                      <Search 
                        className="position-absolute"
                        style={{
                          left: '15px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#6c757d',
                          zIndex: 10
                        }}
                        size={18}
                      />
                    </div>
                  </Col>
                </Row>

                {/* Data Table */}
                {filteredData.length === 0 ? (
                  <Alert color="info" className="text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    No data available.
                  </Alert>
                ) : (
                    <div className="table-responsive">
                      {(() => {
                        const lrsCols = lrsVisibleColumns();
                        const totalTableWidth = lrsCols.reduce(
                          (sum, col) => sum + (columnWidths[col.key] || LRS_DEFAULT_WIDTHS[col.key] || 80),
                          0
                        );
                        return (
                          <Table
                            bordered
                            hover
                            className="mb-0 resizable-table"
                            style={{
                              tableLayout: 'fixed',
                              width: `${totalTableWidth}px`,
                              minWidth: `${totalTableWidth}px`,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <colgroup>
                              {lrsCols.map((col) => {
                                const colW = columnWidths[col.key] || LRS_DEFAULT_WIDTHS[col.key] || 80;
                                return (
                                  <col
                                    key={col.key}
                                    style={{
                                      width: `${colW}px`,
                                      minWidth: `${colW}px`,
                                      maxWidth: `${colW}px`,
                                    }}
                                  />
                                );
                              })}
                            </colgroup>
                            <thead className="table-success">
                              <tr>
                                {lrsCols.map((col) => {
                                  const colW = columnWidths[col.key] || LRS_DEFAULT_WIDTHS[col.key] || 80;
                                  return (
                                    <th
                                      key={col.key}
                                      className={`text-center align-middle${lrsDragOverKey === col.key ? ' col-drag-over' : ''}`}
                                      draggable={col.key !== 'Checkbox' && col.key !== 'Actions'}
                                      onDragStart={col.key !== 'Checkbox' && col.key !== 'Actions' ? (e => lrsDragStart(e, col.key)) : undefined}
                                      onDragOver={e => lrsDragOver(e, col.key)}
                                      onDrop={e => lrsDrop(e, col.key)}
                                      onDragEnd={lrsDragEnd}
                                      style={{
                                        width: `${colW}px`,
                                        minWidth: `${colW}px`,
                                        maxWidth: `${colW}px`,
                                        padding: col.key === 'Checkbox' ? '8px 4px' : '8px',
                                        cursor: col.key !== 'Checkbox' && col.key !== 'Actions' ? 'grab' : 'default',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        userSelect: 'none',
                                        boxSizing: 'border-box',
                                      }}
                                    >
                                      {col.key === 'Checkbox' ? (
                                        <Input
                                          type="checkbox"
                                          checked={selectAll}
                                          onChange={handleSelectAll}
                                          title="Select All"
                                        />
                                      ) : col.key === 'Actions' ? (
                                        <span>Actions</span>
                                      ) : (
                                        <div className="d-flex justify-content-between align-items-center" style={{ pointerEvents: 'none' }}>
                                          <span>{col.label}</span>
                                          <i className="fas fa-grip-vertical" style={{ fontSize: "0.4rem", opacity: 0.5 }} title="Drag to reorder"></i>
                                        </div>
                                      )}
                                      <div
                                        className="col-resize-handle"
                                        onMouseDown={e => handleResizeMouseDown(e, col.key)}
                                        onTouchStart={e => handleResizeMouseDown(e, col.key)}
                                      />
                                    </th>
                                  );
                                })}
                              </tr>
                            </thead>
                            <tbody>
                              {filteredData.map((row, index) => (
                                <tr key={`${row.linkRegisterId}-${index}`}>
                                  {lrsCols.map((col) => {
                                    const colW = columnWidths[col.key] || LRS_DEFAULT_WIDTHS[col.key] || 80;
                                    const cellStyle = {
                                      width: `${colW}px`,
                                      minWidth: `${colW}px`,
                                      maxWidth: `${colW}px`,
                                      boxSizing: 'border-box',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    };
                                    switch (col.key) {
                                      case 'Checkbox':
                                        return (
                                          <td key={col.key} className="text-center align-middle" style={{ ...cellStyle, padding: '8px 4px' }}>
                                            <Input
                                              type="checkbox"
                                              checked={selectedRows.includes(row.linkRegisterId)}
                                              onChange={() => handleRowSelect(row.linkRegisterId)}
                                              onClick={e => e.stopPropagation()}
                                            />
                                          </td>
                                        );
                                      case 'CreatedOn':
                                        return (
                                          <td key={col.key} className="text-center fw-semibold align-middle" style={cellStyle}>
                                            {row.createdOn 
                                              ? new Date(row.createdOn).toLocaleDateString('en-GB', { 
                                                  day: '2-digit', 
                                                  month: '2-digit', 
                                                  year: 'numeric',
                                                  hour: '2-digit',
                                                  minute: '2-digit'
                                                })
                                              : '-'}
                                          </td>
                                        );
                                      case 'Period':
                                        return (
                                          <td key={col.key} className="text-center fw-semibold align-middle" style={cellStyle}>
                                            {row.period || '-'}
                                          </td>
                                        );
                                      case 'LinkQty':
                                        return (
                                          <td key={col.key} className="text-center fw-semibold align-middle" style={cellStyle}>
                                            {row.linkQty ? parseFloat(row.linkQty).toLocaleString() : '0'}
                                          </td>
                                        );
                                      case 'ContractChain':
                                        return (
                                          <td key={col.key} className="align-middle" style={{ ...cellStyle, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                            <span className="fw-semibold">
                                              {renderColoredChain(row.contracts)}
                                            </span>
                                          </td>
                                        );
                                      case 'Actions':
                                        return (
                                          <td key={col.key} className="text-center align-middle" style={cellStyle}>
                                            <Button
                                              color="danger"
                                              size="sm"
                                              onClick={() => btnDeleteOnClick(row.linkRegisterId)}
                                              className="px-2 py-1"
                                              style={{
                                                borderRadius: '6px',
                                                transition: 'all 0.3s ease'
                                              }}
                                              title="Delete Link Register"
                                            >
                                              <Trash2 size={14} />
                                            </Button>
                                          </td>
                                        );
                                      default:
                                        return null;
                                    }
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        );
                      })()}
                    </div>
                )}

                {/* Summary */}
                {filteredData.length > 0 && (
                  <div className="mt-3 text-muted small">
                    <strong>Total Link Registers:</strong> {filteredData.length} | 
                    <strong> Selected:</strong> {selectedRows.length} |
                    <strong> Total Contracts:</strong> {filteredData.reduce((sum, row) => sum + row.totalContracts, 0)} |
                    <strong> Total Link Qty:</strong> {filteredData.reduce((sum, row) => sum + (parseFloat(row.linkQty) || 0), 0).toLocaleString()}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default LinkRegisterShow;
